import { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { getCampaign, updateCampaign } from '../api/campaign';
import { getCompanyPlans } from '../api/plan';
import RegistarLayout from '../components/RegistarLayout';
import InputFormLayout from '../components/InputFormLayout';
import RegistarButtonLayout from '../components/RegistarButtonLayout';
import SelectLayout from '../components/SelectLayout';
import './CampaignRegistar.css';

const CampaignEditPage = () => {
  const { companyId, campaignId } = useParams();
  const [searchParams] = useSearchParams();
  const companyName = searchParams.get('companyName') || `企業ID: ${companyId}`;
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [campaign, setCampaign] = useState(null);
  const [plan, setPlan] = useState(null);
  const [companyPlans, setCompanyPlans] = useState([]);
  const [upgradePlanOptions, setUpgradePlanOptions] = useState([]);

  // フォーム状態
  const [planName, setPlanName] = useState('');
  const [campaignCode, setCampaignCode] = useState('');
  const [upgradeType, setUpgradeType] = useState('0');
  const [upgradePlanId, setUpgradePlanId] = useState('');
  const [exclusivePlan, setExclusivePlan] = useState('');
  const [planType, setPlanType] = useState('premium');
  const [referrerUrls, setReferrerUrls] = useState('');
  const [isMember, setIsMember] = useState('0');
  const [memberIdLabel, setMemberIdLabel] = useState('');
  const [memberIdValidationType, setMemberIdValidationType] = useState('');
  const [memberIdMinDigits, setMemberIdMinDigits] = useState('');
  const [memberIdMaxDigits, setMemberIdMaxDigits] = useState('');
  const [repeatPlanId, setRepeatPlanId] = useState('');

  const [error, setError] = useState(null);
  const [message, setMessage] = useState('');

  // バリデーションチェックNoのオプション（1-15）
  const validationTypeOptions = [
    { value: 1, label: '01:数字のみ' },
    { value: 2, label: '02:英小文字のみ' },
    { value: 3, label: '03:英大文字のみ' },
    { value: 4, label: '04:記号のみ' },
    { value: 5, label: '05:数字、英小文字' },
    { value: 6, label: '06:数字、英大文字' },
    { value: 7, label: '07:数字、記号' },
    { value: 8, label: '08:英小文字、英大文字' },
    { value: 9, label: '09:英小文字、記号' },
    { value: 10, label: '10:英大文字、記号' },
    { value: 11, label: '11:数字、英小文字、英大文字' },
    { value: 12, label: '12:数字、英小文字、記号' },
    { value: 13, label: '13:数字、英大文字、記号' },
    { value: 14, label: '14:英小文字、英大文字、記号' },
    { value: 15, label: '15:数字、英小文字、英大文字、記号' },
  ];

  useEffect(() => {
    const fetchData = async () => {
      try {
        // キャンペーン情報取得
        const campaignRes = await getCampaign(campaignId);
        const campaignData = campaignRes.campaign;
        const planData = campaignData.plan;

        setCampaign(campaignData);
        setPlan(planData);

        // フォーム初期値設定
        setPlanName(campaignData.plan_name || '');
        setCampaignCode(campaignData.campaign_code || '');
        setReferrerUrls(
          campaignData.referrer_urls
            ? campaignData.referrer_urls.join(', ')
            : ''
        );

        // Plan関連の初期値
        if (planData) {
          setUpgradeType(planData.upgrade_type || '0');
          setUpgradePlanId(planData.upgrade_plan_id || '');
          setExclusivePlan(planData.exclusive_plan || '');
          setPlanType(planData.plan_type || 'premium');
          setIsMember(planData.is_member ?? 0);
          setMemberIdLabel(planData.member_id_label || '');
          setMemberIdValidationType(planData.member_id_validation_type || '');
          setMemberIdMinDigits(planData.member_id_min_digits || '');
          setMemberIdMaxDigits(planData.member_id_max_digits || '');
          setRepeatPlanId(planData.repeat_plan_id || '');
        }

        // 企業のプラン一覧取得（アップグレード先、同時契約不可プラン用）
        const plansRes = await getCompanyPlans(companyId);
        setCompanyPlans(plansRes.plans || []);
      } catch (err) {
        console.error('データ取得に失敗:', err);
        setError('データの取得に失敗しました');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [campaignId, companyId]);

  // アップグレード先プランのオプションを更新
  useEffect(() => {
    if (!campaign || !companyPlans.length) {
      setUpgradePlanOptions([]);
      return;
    }

    const options = companyPlans.filter(
      (p) => p.price_id !== campaign.price_id
    );
    setUpgradePlanOptions(options);
  }, [campaign, companyPlans]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setMessage('');

    // 確認ダイアログ
    if (!window.confirm('編集内容を保存します。よろしいですか？')) {
      return;
    }

    try {
      // referrerUrlsをカンマ区切りから配列に変換
      let referrerUrlsArray = null;
      if (referrerUrls && referrerUrls.trim()) {
        referrerUrlsArray = referrerUrls
          .split(',')
          .map((url) => url.trim())
          .filter((url) => url.length > 0);
      }

      const isMemberNumber = Number(isMember);
      const hasMemberSettings = isMemberNumber !== 0;

      const payload = {
        plan_name: planName,
        campaign_code: campaignCode,
        upgrade_type: upgradeType,
        upgrade_plan_id: upgradeType !== '0' ? upgradePlanId || null : null,
        exclusive_plan: exclusivePlan || null,
        plan_type: planType,
        referrer_urls: referrerUrlsArray,
        is_member: isMemberNumber,
        member_id_validation_type:
          hasMemberSettings && memberIdValidationType
          ? Number(memberIdValidationType)
          : null,
        member_id_min_digits:
          hasMemberSettings && memberIdMinDigits
            ? Number(memberIdMinDigits)
            : null,
        member_id_max_digits:
          hasMemberSettings && memberIdMaxDigits
            ? Number(memberIdMaxDigits)
            : null,
        member_id_label:
          hasMemberSettings ? memberIdLabel || null : null,
        repeat_plan_id: hasTrialDays ? repeatPlanId || null : null,
      };

      await updateCampaign(campaignId, payload);
      setMessage('キャンペーンを更新しました！');
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.error || 'キャンペーン更新に失敗しました');
    }
  };

  if (loading) {
    return (
      <RegistarLayout title="キャンペーン編集" margin="20px auto">
        <div>読み込み中...</div>
      </RegistarLayout>
    );
  }

  if (!campaign) {
    return (
      <RegistarLayout title="キャンペーン編集" margin="20px auto">
        <div style={{ color: 'red' }}>キャンペーンが見つかりません</div>
      </RegistarLayout>
    );
  }

  const hasTrialDays = plan?.trial_days > 0;

  return (
    <RegistarLayout title="キャンペーン編集" margin="20px auto">
      {message && (
        <div style={{ color: 'green', marginBottom: '10px' }}>{message}</div>
      )}
      {error && <p className="error">{error}</p>}

      <InputFormLayout
        id="campaignName"
        labelTitle="キャンペーン名"
        type="text"
        value={planName}
        onChange={(e) => setPlanName(e.target.value)}
        placeholder="例: FreeCampaign"
      />

      <InputFormLayout
        id="campaignCode"
        labelTitle="キャンペーンコード"
        type="text"
        value={campaignCode}
        onChange={(e) => setCampaignCode(e.target.value)}
        placeholder="例: 1234"
      />

      {/* キャンペーンプラン: 表示のみ（グレーアウト） */}
      <div style={{ marginBottom: '10px' }}>
        <label
          style={{ color: '#333', display: 'block', marginBottom: '9px' }}
        >
          キャンペーンプラン
        </label>
        <input
          type="text"
          value={plan?.name || campaign.price_id}
          disabled
          style={{
            width: '100%',
            height: '30px',
            margin: '5px 0',
            border: '1px solid #ccc',
            borderRadius: '5px',
            padding: '0 4px',
            backgroundColor: '#e9ecef',
            color: '#6c757d',
          }}
        />
      </div>

      {/* トライアル終了後再契約プラン: トライアル日数がある場合のみ表示 */}
      {hasTrialDays && (
        <SelectLayout
          id="repeatPlan"
          selectTitle="トライアル終了後再契約プラン"
          value={repeatPlanId}
          onChange={(e) => setRepeatPlanId(e.target.value)}
          options={companyPlans.map((p) => ({
            id: p.price_id,
            name: p.name,
            value: p.price_id,
          }))}
        />
      )}

      <SelectLayout
        id="upgradeType"
        selectTitle="アップグレードタイプ"
        value={upgradeType}
        onChange={(e) => setUpgradeType(e.target.value)}
        options={[
          { id: '0', value: '0', name: 'なし' },
          { id: '1', value: '1', name: '1' },
          { id: '2', value: '2', name: '2' },
        ]}
        show={false}
      />

      {/* アップグレード先プランコード: アップグレードタイプが「1」または「2」の場合のみ表示 */}
      {upgradeType !== '0' && (
        <SelectLayout
          id="upgradePlanId"
          selectTitle="アップグレード先プランコード"
          value={upgradePlanId}
          onChange={(e) => setUpgradePlanId(e.target.value)}
          options={upgradePlanOptions.map((p) => ({
            id: p.id,
            name: p.name,
            value: p.price_id,
          }))}
        />
      )}

      <SelectLayout
        id="exclusivePlan"
        selectTitle="同時契約不可プラン"
        value={exclusivePlan || ''}
        onChange={(e) => setExclusivePlan(e.target.value)}
        options={upgradePlanOptions?.map((p) => ({
          id: p.price_id,
          name: p.name,
          value: p.price_id,
        }))}
      />

      <SelectLayout
        id="planSelect"
        selectTitle="料金プラン"
        value={planType}
        onChange={(e) => setPlanType(e.target.value)}
        options={[
          { id: 'premium', value: 'premium', name: 'プレミアム' },
          { id: 'selection', value: 'selection', name: 'セレクション' },
        ]}
        show={false}
      />

      <InputFormLayout
        id="Referrer"
        labelTitle="リファラ元URL（カンマ区切りで複数入力可能）"
        type="text"
        value={referrerUrls}
        onChange={(e) => setReferrerUrls(e.target.value)}
        placeholder="例: https://example.com, https://test.com"
        required={false}
      />

      {/* 会員ID設定 */}
      <SelectLayout
        id="isMember"
        selectTitle="会員ID設定"
        value={isMember}
        onChange={(e) => {
          const value = e.target.value;
          setIsMember(value);

          if (value === '0') {
            setMemberIdLabel('');
            setMemberIdValidationType('');
            setMemberIdMinDigits('');
            setMemberIdMaxDigits('');
          }
          if (value === '2') {
            // 自動の場合はバリデーション不要
            setMemberIdValidationType('');
          }
        }}
        options={[
          { id: '0', value: '0', name: 'なし' },
          { id: '1', value: '1', name: '手動' },
          { id: '2', value: '2', name: '自動' },
        ]}
        show={false}
      />

      {/* 会員ID関連フィールド */}
      {isMember !== '0' && (
        <>
          {/* 注釈（手動・自動 共通） */}
          <InputFormLayout
            id="memberIdLabel"
            labelTitle="会員ID入力フォームの注釈"
            type="text"
            value={memberIdLabel}
            onChange={(e) => setMemberIdLabel(e.target.value)}
            placeholder="例: 会員IDを入力・確認してボタンを押下してください。"
            multiline
          />

          {/* 手動のみ：注釈の直下に表示 */}
          {isMember === '1' && (
            <SelectLayout
              id="memberIdValidationType"
              selectTitle="バリデーションチェックNo"
              value={memberIdValidationType}
              onChange={(e) => setMemberIdValidationType(e.target.value)}
              options={validationTypeOptions.map((option) => ({
                id: option.value,
                name: option.label,
                value: option.value,
              }))}
            />
          )}

          {/* 有効桁数（最小） */}
          <InputFormLayout
            id="memberIdMinDigits"
            labelTitle="有効桁数（最小）"
            type="number"
            min="1"
            value={memberIdMinDigits}
            onChange={(e) => setMemberIdMinDigits(e.target.value)}
            placeholder="例: 1"
          />

          {/* 有効桁数（最大） */}
          <InputFormLayout
            id="memberIdMaxDigits"
            labelTitle="有効桁数（最大）"
            type="number"
            min="1"
            value={memberIdMaxDigits}
            onChange={(e) => setMemberIdMaxDigits(e.target.value)}
            placeholder="例: 20"
          />
        </>
      )}

      <div style={{ marginTop: '30px' }} />
      <RegistarButtonLayout
        buttonTitle="更新"
        type="submit"
        onClick={handleSubmit}
      />

      <RegistarButtonLayout
        buttonTitle="キャンペーン一覧に戻る"
        type="button"
        onClick={() =>
          navigate(
            `/companies/${companyId}?companyName=${encodeURIComponent(companyName)}`
          )
        }
      />
    </RegistarLayout>
  );
};

export default CampaignEditPage;
